import { debounce } from './utils';

export interface EditorMetrics {
  wordCount: number;
  headers: {
    h2: number;
    h3: number;
    h4: number;
  };
}

export function calculateEditorMetrics(content: string): EditorMetrics {
  // Create temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = content;

  // Remove code blocks and tables from word count calculation
  const codeBlocks = tempDiv.querySelectorAll('pre, code, table');
  codeBlocks.forEach(block => {
    block.setAttribute('data-exclude-from-count', 'true');
  });

  // Count words only from elements that are not excluded
  const textNodes = Array.from(tempDiv.childNodes).filter(node => 
    !(node as HTMLElement).hasAttribute?.('data-exclude-from-count')
  );
  
  const text = textNodes.map(node => node.textContent || '').join(' ');
  const wordCount = text.split(/\s+/).filter(word => word.length > 0).length;

  // Count headers
  const h2Count = tempDiv.querySelectorAll('h2').length;
  const h3Count = tempDiv.querySelectorAll('h3').length;
  const h4Count = tempDiv.querySelectorAll('h4').length;

  return {
    wordCount,
    headers: {
      h2: h2Count,
      h3: h3Count,
      h4: h4Count
    }
  };
}

// Calculate metrics directly from Quill instance
export function calculateQuillMetrics(quillInstance: any): EditorMetrics {
  if (!quillInstance || !quillInstance.root) {
    return {
      wordCount: 0,
      headers: { h2: 0, h3: 0, h4: 0 }
    };
  }

  // Get the editor root element
  const editorRoot = quillInstance.root;
  
  // Create a clone to work with
  const tempRoot = editorRoot.cloneNode(true);
  
  // Remove code blocks and tables from word count calculation
  const codeBlocks = tempRoot.querySelectorAll('pre, code, table');
  codeBlocks.forEach((block: HTMLElement) => {
    block.setAttribute('data-exclude-from-count', 'true');
  });
  
  // Get text content excluding code blocks and tables
  const textNodes = Array.from(tempRoot.childNodes).filter((node: any) => 
    !node.hasAttribute || !node.hasAttribute('data-exclude-from-count')
  );
  
  const text = textNodes.map((node: any) => node.textContent || '').join(' ');
  const wordCount = text.split(/\s+/).filter(word => word.length > 0).length;

  // Count headers
  const h2Count = editorRoot.querySelectorAll('h2').length;
  const h3Count = editorRoot.querySelectorAll('h3').length;
  const h4Count = editorRoot.querySelectorAll('h4').length;

  return {
    wordCount,
    headers: {
      h2: h2Count,
      h3: h3Count,
      h4: h4Count
    }
  };
}

// Create debounced version of metrics calculation
export const debouncedCalculateMetrics = debounce((content: string, callback: (metrics: EditorMetrics) => void) => {
  const metrics = calculateEditorMetrics(content);
  callback(metrics);
}, 500); // 500ms delay